import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MIN_TEAMS,
  MAX_TEAMS,
  buildStructure,
  scheduleMatches,
  teamLetter,
  fmtTime,
  fmtTimeInput,
  parseTimeInput,
} from './bracket';
import './Simulator.css';

const ROUND_COLORS = ['#6e95e8', '#b27fe3', '#e67fb8', '#eba254', '#2ed09a', '#5cc6cf'];

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const Simulator = () => {
  const { t } = useTranslation();
  const [teams, setTeams] = useState(10);
  const [mode, setMode] = useState('ll');
  const [dur, setDur] = useState(35);
  const [gap, setGap] = useState(5);
  const [teamRest, setTeamRest] = useState(15);
  const [third, setThird] = useState(false);
  const [ceremony, setCeremony] = useState(true);
  const [ceremonyMin, setCeremonyMin] = useState(20);
  const [courts, setCourts] = useState(() => [
    { name: t('sim.court') + ' 1', start: 540, end: 840 },
    { name: t('sim.court') + ' 2', start: 540, end: 720 },
  ]);

  const struct = useMemo(() => buildStructure(mode, teams), [mode, teams]);
  const sim = useMemo(
    () => scheduleMatches(struct, { courts, dur, gap, teamRest, third, ceremony, ceremonyMin }),
    [struct, courts, dur, gap, teamRest, third, ceremony, ceremonyMin]
  );

  const roundName = (label) => {
    if (label.kind === 'numbered') return t('sim.roundN', { n: label.n });
    if (label.kind === 'third') return t('sim.thirdShort');
    const keys = { 64: 'sim.r64', 32: 'sim.r32', 16: 'sim.r16', 8: 'sim.qf', 4: 'sim.sf', 2: 'sim.final' };
    return t(keys[label.size]);
  };

  const entLabel = (e) => {
    if (!e) return '';
    if (e.t === 'team') return teamLetter(e.s);
    if (e.t === 'winner') return t('sim.winnerOf', { id: e.of });
    if (e.t === 'wseed') return t('sim.prelimWinner', { n: e.s });
    if (e.t === 'll') return t('sim.luckyLoser');
    return '';
  };

  const updateCourt = (i, patch) => {
    setCourts((cs) => cs.map((c, ci) => (ci === i ? { ...c, ...patch } : c)));
  };

  const played = sim.placed.length + sim.unplaced.length;
  const playMinutes = played * dur;
  const phaseKeys = { 3: 'sim.phaseSemis', 7: 'sim.phaseQuarters', 15: 'sim.phaseR16', 31: 'sim.phaseR32' };
  const repRound = struct.repechageAfter >= 0 ? struct.rounds[struct.repechageAfter] : null;
  const matchCount = (n) => (n === 1 ? t('sim.matchOne') : t('sim.matchMany', { count: n }));

  return (
    <div className="tk-sim">
      <div>
        <div className="tk-sim-panel">
          <h2>{t('sim.tournament')}</h2>
          <div className="tk-sim-field">
            <label>{t('sim.teams')}</label>
            <div className="tk-sim-stepper">
              <button type="button" aria-label="-" onClick={() => setTeams((v) => clamp(v - 1, MIN_TEAMS, MAX_TEAMS))}>−</button>
              <span className="tk-sim-val">{teams}</span>
              <button type="button" aria-label="+" onClick={() => setTeams((v) => clamp(v + 1, MIN_TEAMS, MAX_TEAMS))}>+</button>
            </div>
          </div>
          <div className="tk-sim-seg" role="tablist">
            <button type="button" className={mode === 'byes' ? 'tk-sim-on' : ''} onClick={() => setMode('byes')}>
              {t('sim.formatByes')}
            </button>
            <button type="button" className={mode === 'll' ? 'tk-sim-on' : ''} onClick={() => setMode('ll')}>
              {t('sim.formatLL')}
            </button>
          </div>
          <div className="tk-sim-field">
            <label>{t('sim.matchMin')}</label>
            <div className="tk-sim-stepper">
              <button type="button" aria-label="-" onClick={() => setDur((v) => clamp(v - 5, 10, 120))}>−</button>
              <span className="tk-sim-val">{dur}</span>
              <button type="button" aria-label="+" onClick={() => setDur((v) => clamp(v + 5, 10, 120))}>+</button>
            </div>
          </div>
          <div className="tk-sim-field">
            <label>{t('sim.restMin')}</label>
            <div className="tk-sim-stepper">
              <button type="button" aria-label="-" onClick={() => setGap((v) => clamp(v - 5, 0, 60))}>−</button>
              <span className="tk-sim-val">{gap}</span>
              <button type="button" aria-label="+" onClick={() => setGap((v) => clamp(v + 5, 0, 60))}>+</button>
            </div>
          </div>
          <div className="tk-sim-field">
            <label>{t('sim.teamRestMin')}</label>
            <div className="tk-sim-stepper">
              <button type="button" aria-label="-" onClick={() => setTeamRest((v) => clamp(v - 5, 0, 120))}>−</button>
              <span className="tk-sim-val">{teamRest}</span>
              <button type="button" aria-label="+" onClick={() => setTeamRest((v) => clamp(v + 5, 0, 120))}>+</button>
            </div>
          </div>
          <div className="tk-sim-field">
            <label htmlFor="tk-sim-third">{t('sim.thirdPlace')}</label>
            <input id="tk-sim-third" type="checkbox" className="tk-sim-switch" checked={third} onChange={(e) => setThird(e.target.checked)} />
          </div>
          <div className="tk-sim-field">
            <label htmlFor="tk-sim-cer">{t('sim.ceremony')}</label>
            <input id="tk-sim-cer" type="checkbox" className="tk-sim-switch" checked={ceremony} onChange={(e) => setCeremony(e.target.checked)} />
          </div>
          {ceremony && (
            <div className="tk-sim-field">
              <label>{t('sim.ceremonyMin')}</label>
              <div className="tk-sim-stepper">
                <button type="button" aria-label="-" onClick={() => setCeremonyMin((v) => clamp(v - 5, 5, 60))}>−</button>
                <span className="tk-sim-val">{ceremonyMin}</span>
                <button type="button" aria-label="+" onClick={() => setCeremonyMin((v) => clamp(v + 5, 5, 60))}>+</button>
              </div>
            </div>
          )}
          <div className="tk-sim-hint">
            {mode === 'll' && struct.resolvedToByes && (
              <span>{t('sim.hintLLResolved', { n: teams, target: struct.target })}</span>
            )}
            {mode === 'll' && !struct.resolvedToByes && (
              <span>
                {struct.byes > 0
                  ? t('sim.hintLL', { n: teams, target: struct.target, byes: struct.byes, phase: t(phaseKeys[struct.oddPart]) })
                  : t('sim.hintLLNoByes', { n: teams, target: struct.target, phase: t(phaseKeys[struct.oddPart]) })}
              </span>
            )}
            {mode === 'byes' && (
              <span>
                {struct.byes > 0
                  ? t('sim.hintByes', { target: struct.target, byes: struct.byes, played: struct.target / 2 - struct.byes })
                  : t('sim.hintByesNone', { target: struct.target })}
              </span>
            )}
          </div>
        </div>
        <div className="tk-sim-panel">
          <h2>{t('sim.courtsTitle')}</h2>
          {courts.map((c, i) => (
            <div className="tk-sim-court" key={i}>
              <div className="tk-sim-court-row1">
                <input
                  type="text"
                  value={c.name}
                  aria-label={t('sim.court')}
                  onChange={(e) => updateCourt(i, { name: e.target.value })}
                />
                {courts.length > 1 && (
                  <button
                    type="button"
                    className="tk-sim-court-del"
                    aria-label={t('sim.removeCourt')}
                    onClick={() => setCourts((cs) => cs.filter((_, ci) => ci !== i))}
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="tk-sim-court-row2">
                {t('sim.from')}{' '}
                <input type="time" value={fmtTimeInput(c.start)} onChange={(e) => updateCourt(i, { start: parseTimeInput(e.target.value) })} />{' '}
                {t('sim.to')}{' '}
                <input type="time" value={fmtTimeInput(c.end)} onChange={(e) => updateCourt(i, { end: parseTimeInput(e.target.value) })} />
              </div>
            </div>
          ))}
          {courts.length < 6 && (
            <button
              type="button"
              className="tk-sim-addcourt"
              onClick={() => setCourts((cs) => [...cs, { name: t('sim.court') + ' ' + (cs.length + 1), start: 540, end: 780 }])}
            >
              {t('sim.addCourt')}
            </button>
          )}
        </div>
      </div>
      <div>
        <div className="tk-sim-summary">
          <div className="tk-sim-stat">
            <div className="tk-sim-k">{t('sim.matches')}</div>
            <div className="tk-sim-v">
              {played}
              {struct.byes > 0 && <small> + {struct.byes} byes</small>}
            </div>
          </div>
          <div className="tk-sim-stat">
            <div className="tk-sim-k">{t('sim.rounds')}</div>
            <div className="tk-sim-v">
              {struct.rounds.length}
              {third && <small> + {t('sim.thirdShort')}</small>}
            </div>
          </div>
          <div className="tk-sim-stat tk-sim-green">
            <div className="tk-sim-k">{t('sim.endsAt')}</div>
            <div className="tk-sim-v">{sim.endAll !== null ? fmtTime(sim.endAll) : '-'}</div>
          </div>
          <div className="tk-sim-stat">
            <div className="tk-sim-k">{t('sim.playTime')}</div>
            <div className="tk-sim-v">
              {playMinutes >= 60 ? Math.round(playMinutes / 6) / 10 : playMinutes}{' '}
              <small>{playMinutes >= 60 ? 'h' : 'min'}</small>
            </div>
          </div>
        </div>
        {sim.unplaced.length > 0 && (
          <div className="tk-sim-warn">
            {t('sim.warnNoFit', {
              count: sim.unplaced.length,
              list: sim.unplaced.map((u) => u.match.id).join(', '),
              min: sim.unplaced.length * (dur + gap),
            })}
          </div>
        )}
        <h3>{t('sim.schedTitle')}</h3>
        <div className="tk-sim-sched">
          {sim.courts.map((c, ci) => (
            <div className="tk-sim-col" key={ci}>
              <h4>
                {c.name}
                <span>
                  {fmtTime(c.start)} - {fmtTime(c.end)}
                </span>
              </h4>
              {c.slots.length === 0 && <div className="tk-sim-empty">{t('sim.noMatches')}</div>}
              {c.slots.map((s, si) =>
                s.ceremony ? (
                  <div className="tk-sim-m tk-sim-ceremony" key={si}>
                    <span className="tk-sim-t">
                      {fmtTime(s.st)} - {fmtTime(s.en)}
                    </span>
                    <span className="tk-sim-l">{t('sim.ceremonyLabel')}</span>
                  </div>
                ) : (
                  <div
                    className="tk-sim-m"
                    key={si}
                    style={{ borderLeftColor: ROUND_COLORS[s.wave.ri % ROUND_COLORS.length] }}
                  >
                    <span className="tk-sim-t">
                      {fmtTime(s.st)} - {fmtTime(s.en)}
                    </span>
                    <span className="tk-sim-l">
                      {s.match.third ? t('sim.thirdShort') : `${entLabel(s.match.a)} vs ${entLabel(s.match.b)}`}
                      <small>
                        {s.match.third
                          ? t('sim.semiLosers')
                          : `${roundName(s.wave.label)}${s.match.id ? ', ' + s.match.id : ''}`}
                      </small>
                    </span>
                  </div>
                )
              )}
            </div>
          ))}
          {repRound && (
            <div className="tk-sim-repechage">
              {t('sim.repechageNote', { round: roundName(repRound.label) })}
            </div>
          )}
        </div>
        <h3>{t('sim.bracketTitle')}</h3>
        <div className="tk-sim-bracketwrap">
          <div className="tk-sim-bracket">
            {struct.rounds.map((r, ri) => {
              const playedIn = r.matches.filter((m) => !m.bye).length;
              const color = ROUND_COLORS[ri % ROUND_COLORS.length];
              const isLast = ri === struct.rounds.length - 1;
              return (
                <div className="tk-sim-round" key={ri} style={{ '--tk-rc': color }}>
                  <h4>
                    {roundName(r.label)} <span>{matchCount(playedIn)}</span>
                  </h4>
                  {r.matches.map((m, mi) =>
                    m.bye ? (
                      <div className="tk-sim-bm tk-sim-bye" key={mi}>
                        <span className="tk-sim-vs">{t('sim.byeAdvances', { team: entLabel(m.adv) })}</span>
                      </div>
                    ) : (
                      <div className="tk-sim-bm" key={mi}>
                        <div className="tk-sim-id">{m.id}</div>
                        <div className="tk-sim-vs">
                          {m.a.t === 'team' ? entLabel(m.a) : <em>{entLabel(m.a)}</em>}
                          {' vs '}
                          {m.b.t === 'team' ? entLabel(m.b) : <em>{entLabel(m.b)}</em>}
                          {m.b.t === 'll' && <span className="tk-sim-ll-badge">{t('sim.llBadge')}</span>}
                        </div>
                      </div>
                    )
                  )}
                  {third && isLast && (
                    <div className="tk-sim-bm">
                      <div className="tk-sim-id">3P</div>
                      <div className="tk-sim-vs">
                        <em>{t('sim.semiLosers')}</em>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <p className="tk-sim-note">{t('sim.footNote')}</p>
      </div>
    </div>
  );
};

export default Simulator;
